import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { TableCell } from '@design/components/table';
import clsx from 'clsx';
import { ReactNode } from 'react';

import styles from './ColumnTable.module.scss';
import type { ColumnTableCellProps } from './ColumnTable.types';

const isNumberValue = (data: unknown) => typeof data === 'number';

export const ColumnTableCell = <T extends {}>({
  item,
  column,
  alignment,
  hasScrollbar,
  className,
  ...rest
}: ColumnTableCellProps<T>) => {
  let content;

  if (item.isLoading) {
    content = (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '20px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  } else {
    content = column.render
      ? column.render(item[column.key], item)
      : item[column.key];
  }

  // `TableCell` hides falsy children behind an N/A fallback, so stringify
  // numeric content to ensure a meaningful `0` renders instead of "N/A".
  const displayContent =
    typeof content === 'number' ? String(content) : content;

  return column.sticky ? (
    <TableCell
      className={clsx(
        styles.stickyTableCell,
        { [styles.stickyTableCellBorder]: hasScrollbar },
        className
      )}
      alignment={alignment}
      isNumber={isNumberValue(content)}
      {...rest}
    >
      {displayContent as ReactNode}
    </TableCell>
  ) : (
    <TableCell
      className={className}
      alignment={alignment}
      isNumber={isNumberValue(content)}
      {...rest}
    >
      {displayContent as ReactNode}
    </TableCell>
  );
};
